// Linke Hälfte: Wahrscheinlichkeitswolke, Anchor-Stufenlinie, PIPR-Linie.
//
// Die Wolke wird auf schmalen Viewports weggelassen — bei einem Pixel pro Tag
// ist sie nur noch Textur und kostet mehrere tausend Rechtecke.
//
// Die PIPR-Linie trägt die wahrscheinlichste Entscheidung als Farbe: rot, wo der
// Markt eine Anhebung als Modus sieht, blau bei einer Senkung, neutral bei
// keiner Änderung. Die Farben kommen aus `binColor`, also derselben Skala wie
// die Wolke — die Linie sagt damit nichts anderes als die Zelle mit der größten
// Deckkraft direkt darunter, nur ohne dass man sie suchen muss.
//
// Genau deshalb bekommen beide Linien ein Casing: gleiche Farbe wie der
// Untergrund heißt gleiche Farbe wie die Wolke, und ohne Trennung verschwinden
// sie dort, wo sie am meisten zu sagen haben. Derselbe Pfad wird zweimal
// gezeichnet — darunter breiter in Kartenfarbe, darüber die eigentliche Linie.
// Kein Filter, keine zweite Farbbedeutung; Weiß wäre hier schlecht, das ist der
// Ton für "keine Änderung".
//
// Reihenfolge: erst Anker samt Casing, dann PIPR samt Casing. Andersherum
// stanzt das Ankercasing ein Loch in die Hauptlinie.
//
// Dritte Linie, nur wo `pending` von 0 verschieden ist: der beschlossene, aber
// noch nicht wirksame Stand, dazu das Band zwischen beiden. Die EZB kündigt
// sechs Tage vor Wirksamkeit an, die Fed nicht — deshalb hängt das ganze Paket
// an `hasPending` und nicht an der Bank.

import * as d3 from "d3";

import { PALETTE, binColor, binOpacity } from "../../palette";
import { modalTones, toneGradientStops, toneRuns } from "./decisionTone";
import type { ToneRun } from "./decisionTone";
import type { Day, G, Model } from "../model";

const TONE_GRADIENT_ID = "pipr-tone";

const PIPR_WIDTH = 2;
const CASING_WIDTH = 3;          // ergibt 0.5 px Rand auf jeder Seite
const CASING_OPACITY = 0.8;

const ANCHOR_WIDTH = 1.5;
const ANCHOR_OPACITY = 0.7;
const ANCHOR_DASH = "5 1";
const ANCHOR_CASING_WIDTH = 3;

const DECIDED_WIDTH = 1.5;
const DECIDED_OPACITY = 0.9;
const DECIDED_DASH = "2 3";
const DECIDED_CASING_WIDTH = 3;

export function drawHistoryCells(g: G, model: Model): void {
    if (model.layout.isNarrow) return;

    const cells: Array<{ x: number; y: number; c: string; o: number }> = [];
    model.vis.forEach((d) => {
        d.p.forEach((q, j) => {
            if (q < 5) return;
            cells.push({
                x: model.x(d.date),
                y: model.yCellHist(d, j) - model.cellH / 2,
                c: binColor(model.deltas[j]),
                o: binOpacity(q),
            });
        });
    });

    g.selectAll("rect")
        .data(cells)
        .join("rect")
        .attr("x", (c) => c.x)
        .attr("y", (c) => c.y)
        .attr("width", model.dayW)
        .attr("height", Math.max(2, model.cellH))
        .attr("rx", 1)
        .attr("fill", (c) => c.c)
        .attr("opacity", (c) => c.o);
}

/** Farbe eines Laufs: Wolkenskala für Anhebung/Senkung, Tinte für Stillstand. */
function runColor(run: ToneRun): string {
    return run.tone === "hold" ? PALETTE.ink : binColor(run.delta);
}

/**
 * Untergrund für einen Pfad in Kartenfarbe.
 *
 * `dash` muss dem der Linie entsprechen, sonst füllt das Casing die Lücken einer
 * Strichlinie. Linecap bleibt bewusst auf `butt`: mit `round` wüchse jeder
 * Strich um die halbe Breite an beiden Enden und die Lücken gingen zu.
 */
function casing(g: G, d: string, width: number, dash?: string) {
    const path = g
        .append("path")
        .attr("fill", "none")
        .attr("stroke", PALETTE.card)
        .attr("stroke-opacity", CASING_OPACITY)
        .attr("stroke-width", width)
        .attr("d", d);
    if (dash) path.attr("stroke-dasharray", dash);
    else path.attr("stroke-linecap", "round").attr("stroke-linejoin", "round");
    return path;
}

/** Verlauf für die PIPR-Linie anlegen und die zu setzende `stroke` zurückgeben. */
function piprStroke(g: G, model: Model): string {
    const runs = toneRuns(modalTones(model.deltas, model.vis.map((d) => d.p)));
    const xAt = (i: number) => model.x(model.vis[i].date);
    const stops = toneGradientStops(runs, xAt, runColor);

    // Ein einziger Lauf braucht keinen Verlauf, und bei einem einzigen sichtbaren
    // Tag hat der Gradient keine Ausdehnung — dann würde `url(#…)` gar nichts
    // zeichnen statt einer Linie.
    if (stops.length === 0) return PALETTE.ink;
    if (runs.length === 1) return runColor(runs[0]);

    const grad = g
        .append("defs")
        .append("linearGradient")
        .attr("id", TONE_GRADIENT_ID)
        .attr("gradientUnits", "userSpaceOnUse")
        .attr("x1", xAt(0))
        .attr("x2", xAt(model.vis.length - 1))
        .attr("y1", 0)
        .attr("y2", 0);

    grad.selectAll("stop")
        .data(stops)
        .join("stop")
        .attr("offset", (s) => s.offset)
        .attr("stop-color", (s) => s.color);

    return `url(#${TONE_GRADIENT_ID})`;
}

/**
 * Band und Linie des beschlossenen Stands.
 *
 * `defined` schneidet beide auf die Tage mit offenem Schritt zu, sodass je
 * Beschluss ein eigener Abschnitt entsteht. Ohne diesen Schnitt läge die Linie
 * den Rest der Zeit deckungsgleich auf der Ankerlinie.
 */
function drawPending(g: G, model: Model): void {
    const open = (d: Day) => d.pending !== 0;

    const dBand = d3
        .area<Day>()
        .defined(open)
        .x((d) => model.x(d.date))
        .y0((d) => model.yAnchor(d))
        .y1((d) => model.yDecided(d))
        .curve(d3.curveStepAfter)(model.vis) as string;

    const dDecided = d3
        .line<Day>()
        .defined(open)
        .x((d) => model.x(d.date))
        .y((d) => model.yDecided(d))
        .curve(d3.curveStepAfter)(model.vis) as string;

    if (dBand) {
        g.append("path").attr("fill", PALETTE.pendingBand).attr("stroke", "none").attr("d", dBand);
    }

    if (!dDecided) return;

    casing(g, dDecided, DECIDED_CASING_WIDTH, DECIDED_DASH);

    g.append("path")
        .attr("fill", "none")
        .attr("stroke", PALETTE.ink)
        .attr("stroke-opacity", DECIDED_OPACITY)
        .attr("stroke-width", DECIDED_WIDTH)
        .attr("stroke-dasharray", DECIDED_DASH)
        .attr("d", dDecided);
}

export function drawHistoryLines(g: G, model: Model): void {
    // Jeden Pfad einmal rechnen, zweimal zeichnen — Casing und Linie müssen
    // exakt deckungsgleich sein, sonst blitzt der Rand einseitig durch.
    const dAnchor = d3
        .line<Day>()
        .x((d) => model.x(d.date))
        .y((d) => model.yAnchor(d))
        .curve(d3.curveStepAfter)(model.vis) as string;

    const dPipr = d3
        .line<Day>()
        .x((d) => model.x(d.date))
        .y((d) => model.yPipr(d))(model.vis) as string;

    if (model.hasPending) drawPending(g, model);

    casing(g, dAnchor, ANCHOR_CASING_WIDTH, ANCHOR_DASH);

    g.append("path")
        .attr("fill", "none")
        .attr("stroke", PALETTE.ink)
        .attr("stroke-opacity", ANCHOR_OPACITY)
        .attr("stroke-width", ANCHOR_WIDTH)
        .attr("stroke-dasharray", ANCHOR_DASH)
        .attr("d", dAnchor);

    casing(g, dPipr, CASING_WIDTH);

    g.append("path")
        .attr("fill", "none")
        .attr("stroke", piprStroke(g, model))
        .attr("stroke-width", PIPR_WIDTH)
        .attr("stroke-linecap", "round")
        .attr("stroke-linejoin", "round")
        .attr("d", dPipr);
}